import { Action } from 'redux';
import {
  DraftsFetchInfoFailed,
  DraftsFetchInfoRequested,
  DraftsFetchInfoSucceeded,
  DraftsInfoAvailable,
  DraftFetchByParentIdRequested,
  DraftFetchByParentIdSucceeded,
  DraftFetchByParentIdFailed,
  ClearDraftsState
} from '../../app/actions/AppActionTypes';
import { isActionFactory } from '../../app/actions/isActionFactory';
import { SpaceType } from '../../app/state/SpaceInfo';
import { ContentDraftStatus } from '../../ts/ServerApi/types/ContentDraft';
import { GetDraftsRequestParams, GetDraftsByIdRequestParams } from '../requests/DraftsRequests';
import { Draft, DraftContent } from '../types/DraftsTypes';

export interface FetchDraftsSucceededAction extends Action {
  drafts: Draft[];
}

export interface DraftsInfoAvailableAction extends Action {
  contents: DraftContent[];
}

export interface FetchDraftsFailedAction extends Action {
  error: Error;
}

export interface ClearDraftsStateAction extends Action { }

export interface FetchDraftsRequestedAction extends Action {
  data: GetDraftsRequestParams;
  spaceType: SpaceType;
  description: string;
  drafts: Draft[];
}

export interface FetchDraftByParentIdSucceededAction extends Action {
  draft: Draft;
}

export interface FetchDraftByParentIdFailedAction extends Action {
  error: Error;
}

export interface FetchDraftByParentIdRequestedAction extends Action {
  spaceType: SpaceType;
  parentLuid: string;
  data: GetDraftsByIdRequestParams;
}

export function clearDraftState(): ClearDraftsStateAction {
  return {
    type: ClearDraftsState
  };
}

export function fetchDraftsRequested(spaceType: SpaceType, projectLuid?: string): FetchDraftsRequestedAction {
  return {
    type: DraftsFetchInfoRequested,
    data: { projectLuid },
    spaceType: spaceType,
    description: 'Drafts',
    drafts: [],
  };
}

export function fetchDraftsSucceeded(result: Draft[]): FetchDraftsSucceededAction {
  return {
    type: DraftsFetchInfoSucceeded,
    drafts: result,
  };
}

export function fetchDraftsFailed(e: Error): FetchDraftsFailedAction {
  return {
    type: DraftsFetchInfoFailed,
    error: e,
  };
}

export function fetchDraftByParentIdRequested(spaceType: SpaceType, parentLuid: string, parentRevisionId?: string): FetchDraftByParentIdRequestedAction {
  return {
    type: DraftFetchByParentIdRequested,
    spaceType,
    parentLuid,
    data: { parentRevisionId }
  };
}

export function fetchDraftByParentIdSucceeded(draft: Draft): FetchDraftByParentIdSucceededAction {
  return {
    type: DraftFetchByParentIdSucceeded,
    draft,
  };
}

export function fetchDraftByParentIdFailed(e: Error): FetchDraftByParentIdFailedAction {
  return {
    type: DraftFetchByParentIdFailed,
    error: e,
  };
}

export function draftsAvailable(result: Draft[]): DraftsInfoAvailableAction {
  return {
    type: DraftsInfoAvailable,
    contents: convertDraftsToDraftsContent(result),
  };
}

function convertDraftsToDraftsContent(drafts: Draft[]): DraftContent[] {
  let draftContentList: DraftContent[] = [];

  drafts.forEach(draft => {
    const { parentContentLuid, draftLuid } = draft;
    draftContentList.push({
      draftLuid,
      draftStatus: parentContentLuid ? ContentDraftStatus.unpublishedchanges : ContentDraftStatus.unpublished,
      parentContentLuid,
    });
  });

  return draftContentList;
}

export const isFetchDraftsRequestedAction = isActionFactory<FetchDraftsRequestedAction>(DraftsFetchInfoRequested);
export const isFetchDraftsSucceededAction = isActionFactory<FetchDraftsSucceededAction>(DraftsFetchInfoSucceeded);
export const isFetchDraftsFailedAction = isActionFactory<FetchDraftsFailedAction>(DraftsFetchInfoFailed);

export const isClearDraftsStateAction = isActionFactory<ClearDraftsStateAction>(ClearDraftsState);
export const isFetchDraftByParentIdFailedAction = isActionFactory<FetchDraftByParentIdFailedAction>(DraftFetchByParentIdFailed);
export const isFetchDraftByParentIdSucceededAction = isActionFactory<FetchDraftByParentIdSucceededAction>(DraftFetchByParentIdSucceeded);
export const isFetchDraftByParentIdRequestedAction = isActionFactory<FetchDraftByParentIdRequestedAction>(DraftFetchByParentIdRequested);
export const isDraftsInfoAvailable = isActionFactory<DraftsInfoAvailableAction>(DraftsInfoAvailable);
